import { type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

interface PageHeaderProps {
  /**
   * Page title. Accepts a `ReactNode` (not just a string) so a detail page can
   * style part of the heading — e.g. render a device serial in `font-mono` —
   * while still rendering inside the same `<h1>`.
   */
  title: ReactNode;
  subtitle?: string;
  backTo?: string;
  /** The search the back link restores — a list's tab, say. */
  backSearch?: Record<string, string>;
  backLabel?: string;
  actions?: ReactNode;
  /**
   * Optional meta row rendered below the title (and any subtitle).
   * Use for the badge/chip set composed by detail-page meta rows per
   * Carbon detail-page-header guidance: status chips, owner chip,
   * count chips, and similar above-the-fold signal.
   */
  meta?: ReactNode;
}

export function PageHeader({
  title,
  subtitle,
  backTo,
  backSearch,
  backLabel,
  actions,
  meta,
}: PageHeaderProps) {
  const { t } = useTranslation("common");

  // Below sm the header row and its actions wrap, a long title breaks instead of
  // overflowing, and the back link is a 44 px target. Those tokens are all
  // max-sm:, so from sm the header is the desktop one, untouched.
  return (
    <div className="flex items-start justify-between max-sm:flex-wrap max-sm:gap-3">
      <div className="max-sm:min-w-0 max-sm:break-words">
        {backTo && (
          <Link
            to={backTo}
            search={backSearch}
            className="mb-2 inline-flex items-center gap-1 text-sm text-muted hover:text-on-surface max-sm:min-h-11"
          >
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18"
              />
            </svg>
            {backLabel ?? t("back")}
          </Link>
        )}
        <h1 className="text-3xl font-bold text-on-surface">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-muted">{subtitle}</p>}
        {meta && <div className="mt-2">{meta}</div>}
      </div>
      {actions && <div className="flex items-center gap-2 max-sm:flex-wrap">{actions}</div>}
    </div>
  );
}
